import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { chatbotAdminService } from '@/api/chatbot-admin';
import type { AdminLog, AdminLogsListResponse, AdminLogDetail } from '@/types/chatbot-admin';
import { SimpleTablePagination } from '@/components/simple-table-pagination';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Main } from '@/components/layout/main';
import { superAdminService } from '@/api/super-admin';
import type { SuperAdminCabinet } from '@/types/super-admin';
import { LogsStatsDashboard } from '@/components/chatbot/logs-stats-dashboard';
import { timestamp_to_date } from '@/utils/time';

export default function ChatbotLogsAdmin() {
  const { t } = useTranslation();
  const [logs, setLogs] = useState<AdminLog[]>([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [ordering, setOrdering] = useState<string>('');
  const [cabinet, setCabinet] = useState<string>('');
  const [cabinets, setCabinets] = useState<SuperAdminCabinet[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedLog, setSelectedLog] = useState<AdminLogDetail | null>(null);
  const [showDialog, setShowDialog] = useState(false);
  const [activeTab, setActiveTab] = useState<'logs' | 'stats'>('stats');

  const fetchLogs = async () => {
    setLoading(true);
    setError(null);
    try {
      const params: any = { ordering, page, page_size: pageSize };
      if (cabinet) params.cabinet = cabinet;
      const data: AdminLogsListResponse = await chatbotAdminService.getLogs(params);
      setLogs(data.results || []);
      setCount(data.count || 0);
    } catch (e: any) {
      setError(e.message || 'Failed to load logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ordering, page, pageSize, cabinet]);

  useEffect(() => {
    const fetchCabinets = async () => {
      try {
        const data = await superAdminService.getCabinets();
        const list = Array.isArray(data) ? data : (data as any)?.results || [];
        setCabinets(list);
      } catch (err) {
        setCabinets([]);
      }
    };
    fetchCabinets();
  }, []);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return logs;
    return logs.filter(l =>
      (l.user?.name || '').toLowerCase().includes(s) ||
      (l.user_message || '').toLowerCase().includes(s) ||
      (l.response_status || '').toLowerCase().includes(s)
    );
  }, [logs, search]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  const handleView = async (id: string) => {
    try {
      const detail = await chatbotAdminService.getLogDetail(id);
      setSelectedLog(detail);
      setShowDialog(true);
    } catch (e: any) {
      setError(e.message || 'Failed to load log detail');
    }
  };

  return (
    <>
      <Main>
        <div className='mb-2 flex items-center justify-between space-y-2'>
          <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'logs' | 'stats')}>
            <TabsList className="grid w-full max-w-md grid-cols-2">
              <TabsTrigger value="stats">{t('statistics')}</TabsTrigger>
              <TabsTrigger value="logs">{t('logs_list')}</TabsTrigger>
            </TabsList>

            <TabsContent value="stats" className="mt-4">
              <LogsStatsDashboard initialCabinet={cabinet} />
            </TabsContent>

            <TabsContent value="logs" className="mt-4">
              <Card>
                <CardHeader>
                  <CardTitle>{t('super_admin_logs')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    <Input
                      placeholder={t('search_logs')}
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="min-w-[240px]"
                    />
                    <Select value={ordering || 'none'} onValueChange={(v) => setOrdering(v === 'none' ? '' : v)}>
                      <SelectTrigger className="w-[220px]">
                        <SelectValue placeholder={t('ordering')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">{t('default_order')}</SelectItem>
                        <SelectItem value="-created_at">{t('newest_first')}</SelectItem>
                        <SelectItem value="created_at">{t('oldest_first')}</SelectItem>
                        <SelectItem value="-processing_time_ms">{t('longest_processing')}</SelectItem>
                        <SelectItem value="processing_time_ms">{t('shortest_processing')}</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={cabinet || 'none'} onValueChange={(v) => setCabinet(v === 'none' ? '' : v)}>
                      <SelectTrigger className="w-[240px]">
                        <SelectValue placeholder={t('cabinet')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">{t('all_cabinets')}</SelectItem>
                        {cabinets.map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{t('user')}</TableHead>
                          <TableHead>{t('user_message')}</TableHead>
                          <TableHead>{t('response_status')}</TableHead>
                          <TableHead>{t('input_tokens')}</TableHead>
                          <TableHead>{t('output_tokens')}</TableHead>
                          <TableHead>{t('total_tokens')}</TableHead>
                          <TableHead>{t('created_at')}</TableHead>
                          <TableHead>{t('actions')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {loading && (
                          <TableRow>
                            <TableCell colSpan={7}>{t('loading')}</TableCell>
                          </TableRow>
                        )}
                        {error && !loading && (
                          <TableRow>
                            <TableCell colSpan={7} className="text-destructive">{error}</TableCell>
                          </TableRow>
                        )}
                        {!loading && !error && filtered.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={7}>{t('no_logs_found')}</TableCell>
                          </TableRow>
                        )}
                        {filtered.map((l) => (
                          <TableRow key={l.id}>
                            <TableCell>{l.user?.name || '-'}</TableCell>
                            <TableCell className="max-w-[280px] truncate" title={l.user_message}>{l.user_message}</TableCell>
                            <TableCell>
                              <Badge variant={l.response_status === 'success' ? 'default' : 'secondary'}>
                                {t(l.response_status)}
                              </Badge>
                            </TableCell>
                            <TableCell>{l.input_tokens ?? '-'}</TableCell>
                            <TableCell>{l.output_tokens ?? '-'}</TableCell>
                            <TableCell>{(l.total_tokens ?? l.estimated_tokens) ?? '-'}</TableCell>
                            <TableCell>{l.created_at ? timestamp_to_date(Number(l.created_at)).toLocaleString() : '-'}</TableCell>
                            <TableCell>
                              <Button variant="outline" size="sm" onClick={() => handleView(l.id)}>
                                {t('view_details')}
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  <div className="mt-3">
                    <SimpleTablePagination
                      totalItems={count}
                      page={page}
                      pageSize={pageSize}
                      onPageChange={setPage}
                      onPageSizeChange={setPageSize}
                    />
                  </div>
                </CardContent>
              </Card>

              <Dialog open={showDialog} onOpenChange={setShowDialog}>
                <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>{t('log_details')}</DialogTitle>
                  </DialogHeader>
                  {selectedLog ? (
                    <div className="space-y-2">
                      <div>
                        <div className="text-sm text-muted-foreground">{t('user_message')}</div>
                        <div className="text-sm whitespace-pre-wrap break-words">{selectedLog.user_message}</div>
                      </div>
                      <div>
                        <div className="text-sm text-muted-foreground">{t('ai_response')}</div>
                        <div className="text-sm whitespace-pre-wrap break-words">{selectedLog.ai_response}</div>
                      </div>
                      <div>
                        <div className="text-sm text-muted-foreground">{t('full_prompt')}</div>
                        <div className="text-xs whitespace-pre-wrap break-words bg-muted p-2 rounded">{selectedLog.full_prompt}</div>
                      </div>
                    </div>
                  ) : (
                    <div>{t('loading')}</div>
                  )}
                </DialogContent>
              </Dialog>
            </TabsContent>
          </Tabs>
        </div>
      </Main>
    </>
  );
}